import { useState, useCallback, useRef, useEffect } from 'react';
import update from 'immutability-helper';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Training,
  Evaluation,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
} from '@teo/components/icons';
import { IconTile } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import { deep_copy } from '@/util';
import _ from 'lodash';

import { useDnD } from '../DnDContext';

export const StaticCard = ({ node, handleClick }) => {
  const [isHover, setIsHover] = useState(false);

  const [_, setType] = useDnD();

  const onDragStart = (event, node) => {
    setType(node);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      className="my-1 rounded"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onDragStart={(event) => onDragStart(event, node)}
      draggable
    >
      <div
        className={`flex items-center rounded border border-grey-01 p-1 ${
          node?.active ? 'border-secondary-04' : 'border-grey-01'
        }
        ${isHover && node?.active ? 'bg-grey-01' : 'bg-grey-01'}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClick(e, node);
        }}
      >
        {node?.type === 'course' ? (
          node?.picture ? (
            <img
              src={node?.picture}
              className="h-[48px] w-[56px] rounded object-cover"
            />
          ) : (
            <IconTile
              Icon={
                node?.subType === 'course'
                  ? DuoToneCourseFilled
                  : node?.subType === 'assessment'
                  ? DuoToneAssessmentFilled
                  : DuoToneVacancyFilled
              }
              size="large_sm"
              color={
                node?.subType === 'course'
                  ? 'yellow'
                  : node?.subType === 'assessment'
                  ? 'red'
                  : 'yellow'
              }
              className={
                node?.subType === 'course'
                  ? 'fill-primary-06 text-primary-01'
                  : node?.subType === 'assessment'
                  ? 'fill-tint-red-dark text-tint-red-light'
                  : 'fill-error-04 text-error-04'
              }
            />
          )
        ) : (
          <IconTile
            size="large_sm"
            Icon={
              node?.subType === 'training'
                ? Training
                : node?.subType === 'evaluation'
                ? Evaluation
                : Training
            }
            color={
              node?.subType === 'training'
                ? 'green'
                : node?.subType === 'evaluation'
                ? 'purple'
                : 'green'
            }
          />
        )}
        <p className="ml-2 max-w-[270px] break-words text-left text-sm text-base font-medium text-black line-clamp-2">
          {node?.name}
        </p>
        <div className="ml-auto ">
          <img
            src={`/images/icons/drug_dark.svg`}
            className={`h-8 w-8 ${
              isHover && node?.active ? 'opacity-50' : 'opacity-0'
            }`}
            alt="icon"
          />
        </div>
      </div>
    </div>
  );
};

export const Card = ({
  node,
  id,
  index,
  moveCard,
  endMoveCard,
  handleClick,
  nodeID,
}) => {
  const ref = useRef(null);

  const [_, setType] = useDnD();

  const [isHover, setIsHover] = useState(false);
  const [isOutsideDropZone, setIsOutsideDropZone] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    containerRef.current = document.querySelector(`.drop-container_${nodeID}`);
  }, []);

  const checkOutsideDropZone = (event) => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const { clientX, clientY } = event;

    const outside =
      clientX < containerRect.left ||
      clientX > containerRect.right ||
      clientY < containerRect.top ||
      clientY > containerRect.bottom;

    if (outside && !isOutsideDropZone) {
      setIsOutsideDropZone(true);
      setType(node);
    } else if (!outside && isOutsideDropZone) {
      setIsOutsideDropZone(false);
      setType(null);
    }
  };

  const onDrag = (event) => {
    checkOutsideDropZone(event);
  };

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item) => {
      endMoveCard();
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div
      ref={ref}
      style={{ opacity }}
      data-handler-id={handlerId}
      className="relative z-10 my-1 rounded"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onDrag={onDrag}
      draggable
    >
      <div
        className={`flex items-center rounded border border-grey-01 p-1 ${
          node?.active ? 'border-secondary-04' : 'border-grey-01'
        }
        ${isHover && node?.active ? 'bg-grey-01' : 'bg-grey-01'}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClick(e, node);
        }}
      >
        {node?.type === 'course' ? (
          node?.picture ? (
            <img
              src={node?.picture}
              className="h-[48px] w-[56px] rounded object-cover"
            />
          ) : (
            <IconTile
              Icon={
                node?.subType === 'course'
                  ? DuoToneCourseFilled
                  : node?.subType === 'assessment'
                  ? DuoToneAssessmentFilled
                  : DuoToneVacancyFilled
              }
              size="large_sm"
              color={
                node?.subType === 'course'
                  ? 'yellow'
                  : node?.subType === 'assessment'
                  ? 'red'
                  : 'yellow'
              }
              className={
                node?.subType === 'course'
                  ? 'fill-primary-06 text-primary-01'
                  : node?.subType === 'assessment'
                  ? 'fill-tint-red-dark text-tint-red-light'
                  : 'fill-error-04 text-error-04'
              }
            />
          )
        ) : (
          <IconTile
            size="large_sm"
            Icon={
              node?.subType === 'training'
                ? Training
                : node?.subType === 'evaluation'
                ? Evaluation
                : Training
            }
            color={
              node?.subType === 'training'
                ? 'green'
                : node?.subType === 'evaluation'
                ? 'purple'
                : 'green'
            }
          />
        )}
        <p className="ml-2 max-w-[270px] break-words text-left text-sm text-base font-medium text-black line-clamp-2">
          {node?.name}
        </p>
        <div className="ml-auto ">
          <img
            src={`/images/icons/drug_dark.svg`}
            className={`h-8 w-8 ${
              isHover && node?.active ? 'opacity-50' : 'opacity-0'
            }`}
            alt="icon"
          />
        </div>
      </div>
    </div>
  );
};

const DropContainer = ({
  items,
  setItems,
  nodeRoot,
  handleClick,
  endMoveCard,
  nodeID,
}) => {
  {
    const moveCard = useCallback((dragIndex, hoverIndex) => {
      setItems((prevCards) => {
        const updatedCards = update(prevCards, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevCards[dragIndex]],
          ],
        });

        updatedCards?.map((el, i) => {
          el.roots = i === 0 ? [nodeRoot?.id] : [updatedCards[i - 1]?.id];
        });

        return [...updatedCards];
      });
    }, []);

    const renderCard = useCallback((card, index) => {
      return (
        <Card
          node={card}
          key={card.id}
          index={index}
          id={card.order}
          moveCard={moveCard}
          endMoveCard={endMoveCard}
          handleClick={handleClick}
          nodeID={nodeID}
        />
      );
    }, []);

    return (
      <>
        <div>{items.map((card, i) => renderCard(card, i))}</div>
      </>
    );
  }
};

const ThemeTree = ({ nedesLP, nodeRoot, node, setNodes }) => {
  const [items, setItems] = useState(nedesLP || []);
  const prevNedesLP = deep_copy(nedesLP);

  const handleClick = async (e, item) => {
    await setNodes((prev) =>
      prev?.map((nds) => {
        if (nds?.id == node?.id) {
          const updChild = items?.map((el) =>
            el?.id === item?.id
              ? { ...el, active: true }
              : { ...el, active: false }
          );
          return {
            ...nds,
            selected: false,
            data: { ...nds?.data, child: [...updChild, nodeRoot] },
          };
        } else {
          return {
            ...nds,
            selected: false,
            data: {
              ...nds?.data,
              child: nds?.data?.child?.map((el) => ({ ...el, active: false })),
            },
          };
        }
      })
    );
    await node?.data?.setThemeNodeSelected(item);
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const content = document.querySelector('.react-flow');
      if (content.contains(e.target)) {
        setNodes((prev) =>
          prev?.map((nds) => ({
            ...nds,
            data: {
              ...nds?.data,
              child: nds?.data?.child?.map((el) => ({ ...el, active: false })),
            },
          }))
        );
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const endMoveCard = async () => {
    if (items?.length > 0) {
      const editLearningpathId = items[0]?.learningpathId;
      prevNedesLP?.map(async (el) => {
        const newEl = items?.find((nd) => nd?.id === el?.id);
        const arraysAreEqual = (arr1, arr2) => _.isEqual(arr1, arr2);
        if (!arraysAreEqual(el?.roots, newEl?.roots)) {
          await axios.put(
            `${config.backend}/learningpathnode/${editLearningpathId}/${el.id}`,
            {
              roots: newEl?.roots,
            }
          );
        }
      });
      await setNodes((prev) =>
        prev?.map((el) =>
          el?.data?.nodeID == editLearningpathId
            ? { ...el, data: { ...el?.data, child: [...items, nodeRoot] } }
            : el
        )
      );
    }
  };

  if (!items) return null;

  return (
    // <DndProvider backend={HTML5Backend}>
    <DropContainer
      items={items}
      setItems={setItems}
      nodeRoot={nodeRoot}
      handleClick={handleClick}
      endMoveCard={endMoveCard}
      nodeID={node?.id}
    />
    // </DndProvider>
  );
};

export default ThemeTree;
